import { useCallback, useContext, useEffect, useMemo } from 'react'
import { Select } from '@components/formFields/select'
import { templateContext } from '@contexts'
import { TemplateItem } from '../types'
import { t } from '@lingui/core/macro'

interface TemplatePickerProps {
  selectedTemplateIds: number[]
  onSelect: (selected: TemplateItem[]) => void
}

export const TemplatePicker = ({ selectedTemplateIds, onSelect }: TemplatePickerProps) => {
  const { byId: templatesById, fetchTemplates } = useContext(templateContext)

  const templateOptions = useMemo(
    () =>
      Object.values(templatesById)
        .map(template => ({
          name: template.name,
          id: Number(template.id),
        }))
        .sort((a, b) => a.name.localeCompare(b.name)) || [],
    [templatesById],
  )

  useEffect(() => {
    void fetchTemplates()
  }, [fetchTemplates])

  const handleSelect = useCallback(
    (selected: (number | string)[]) => {
      const selectedItems = selected
        .map(id => templatesById[id])
        .filter(template => !!template)
        .map(
          (template): TemplateItem => ({
            id: Number(template.id),
            title: template.name,
            subtitle: `Created ${new Date(template.created_at).toLocaleDateString('en-US', {
              year: '2-digit',
              month: 'numeric',
              day: 'numeric',
            })}`,
          }),
        )

      onSelect(selectedItems)
    },
    [templatesById, onSelect],
  )

  return (
    <>
      <label htmlFor="select-template-button" className="text-slate-800 text-sm">
        {t`Select templates`}
      </label>
      <Select
        name="Select template(s)"
        popoverId="select-template"
        data={templateOptions}
        isLoading={false}
        selectedItems={selectedTemplateIds}
        multi={true}
        onSelectCallback={handleSelect}
      >
        <Select.VirtualList>
          {(index, template) => {
            return (
              <Select.Option key={template.id} id={template.id} index={index} checkbox={true}>
                {template.name}
              </Select.Option>
            )
          }}
        </Select.VirtualList>
      </Select>
    </>
  )
}
